import { Component, computed, Signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { LocalStorageService } from '../shared/local-storage.service';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { Gear } from '../shared/gear.model';
import { GearSelectDialogComponent } from '../gear-select-dialog/gear-select-dialog.component';
import { GearEditDialogComponent } from '../gear-edit-dialog/gear-edit-dialog.component';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { CommonFunctionService } from '../shared/common-function-service.service';
import { AddFisheryDialogComponent } from '../add-fishery-dialog/add-fishery-dialog.component';
import { LookupItem } from '../shared/lookup-item.model';
import { VesselData } from '../vessel-reg/vessel-data.model';
import { PwaUpdateService } from '../shared/update-service.service';

let SCREEN_WIDTH: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
let SCREEN_HEIGHT: string = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0).toString() + 'px';

/**
 * Settings page. Here we see the vessels, favorite species, gears.
 * Check for updates and allow manual sync here.
 */
@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.css'],
    standalone: false
})
export class SettingsComponent {

  //Restricted to groundfish for mow
  favSpeciesList: Signal<LookupItem[]>;
  operatedVessels: Signal<VesselData[]>;
  syncDate: Signal<string>;

  constructor(
    public dataSync: LocalStorageService,
    public router: Router,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private onlineStatus: OnlineStatusService,
    protected common: CommonFunctionService,
    private updater: PwaUpdateService) {
      this.syncDate = computed(() => {
        let syncDate = new Date();
        let lastSyncTime = dataSync.last_sync();
        if (lastSyncTime.getDay() == syncDate.getDay() && 
            lastSyncTime.getMonth() == syncDate.getMonth() && 
            lastSyncTime.getFullYear() == syncDate.getFullYear()) {
              return formatDate(lastSyncTime, 'mediumTime', 'en-US');
        } else return formatDate(lastSyncTime, 'medium', 'en-US'); 
      });

      this.favSpeciesList = computed(() => this.dataSync.current_user().favoriteSpecies);
      this.operatedVessels = computed(() => this.dataSync.current_user().operatedVessels);
  }


  /**
   * "Check for Update" button selected. Same procedure run in app.component constructor.
   */ 
  checkForUpdate(): void {

    console.log('Checking for update...');

    if (this.onlineStatus.getStatus() === OnlineStatusType.OFFLINE) {
      this.toastr.warning('Internet connection is required to check for updates.', 'Error');
      return;
    }

    // status online, check or update
    this.toastr.warning('', 'Checking for update(s)...');
    this.updater.checkForUpdate().then((update: boolean) => {

      // new app version found, updating on reload
      if (update) {
        this.toastr.success('New version found, reloading...', 'Update ready');
        document.location.reload();
      }

      else {
        this.toastr.success('You are using the latest version', 'App on latest version');
      }
    }).catch(() => {
      this.toastr.error('Cannot check for updates at this time', 'Error');
    });
  }

  /**
   * Add a vessel in Settings
   */ 
  addVessel(): void {
    this.common.addVessel(-1, false).catch((err: any) => console.log(err));
  }


  /**
   * Edit an existing vessel in Settings
   */ 
  editVessel(index: number): void {
    this.common.addVessel(index, true).catch((err: any) => console.log(err));

  }


  /**
   * Add new favorite species in Settings
   */ 
  addSpecies(): void {
    const user = this.dataSync.current_user();
    let fishery: "Combined" | "Groundfish" | "HMS" | "Trawl" = user.hmsFishery && user.groundfishFishery ? "Combined" : 
    user.hmsFishery ? "HMS" : user.groundfishFishery ? "Groundfish" : "Trawl";
    this.common.addSpecies(-1, fishery);
  }

  /**
   * Editing an existing
   * @param index Index into user's favorite species array
   */
  editSpecies(index: number): void {
    this.common.addSpecies(index, "Combined");
  }


  /**
 * 
 */
  addRemoveFishery(): void {
    const dialogRef = this.dialog.open(AddFisheryDialogComponent, {
      data: {
        groundfishFishery: this.dataSync.current_user().groundfishFishery,
         hmsFishery: this.dataSync.current_user().hmsFishery,
          trawlFishery: this.dataSync.current_user().trawlFishery}});
    dialogRef.afterClosed().subscribe(selectedOption => {
      this.dataSync.current_user.update(user => {
        switch (selectedOption) {
          case 'groundfishNonTrawl':
            user.groundfishFishery = true;
            user.hmsFishery = false;
            user.trawlFishery = false;
            break;

          case 'hms':
            user.groundfishFishery = false;
            user.hmsFishery = true;
            user.trawlFishery = false;
            break;

          case 'both':
            user.groundfishFishery = true;
            user.hmsFishery = true;
            user.trawlFishery = false;
            break;

          case 'trawl':
            user.groundfishFishery = false;
            user.hmsFishery = false;
            user.trawlFishery = true;
            break;
        }
        return user;
      });
      this.dataSync.updateUser(true, false);
    });
  }


  /**
   * Add a new gear as in set-detail component
   */
  addGear(): void {

    // dialog dimensions
    let w: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
    let h: string = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0).toString() + 'px';

    const dialogRef: MatDialogRef<GearSelectDialogComponent, any> = this.dialog.open(GearSelectDialogComponent, {
      width: SCREEN_WIDTH > w ? SCREEN_WIDTH : w,
      height: SCREEN_HEIGHT > h ? SCREEN_HEIGHT : h,
      data: { screen: "Settings", gearIndex: -1, setType: '' }
    });


    dialogRef.afterClosed().subscribe((result: any) => {
      if (result >= 0) {
        localStorage.setItem('last_gear', result.gearID);
      }
    });
  }

  /**
   * Edit an existing gear
   * @param index Index into user gear array
   */
  editGear(index: number): void {

    let currGear: Gear = this.dataSync.user_gear()[index];

    const dialogRef: MatDialogRef<GearEditDialogComponent, any> = this.dialog.open(GearEditDialogComponent, {
      autoFocus: false,
      width: '425px',
      data: {index: index, isHMS: currGear.fishery == 'HMS'}
    });

    // update gear list in settings
    dialogRef.afterClosed().subscribe((result: any) => {

      if (result) {

        // edit gear
        if (result == 'edit') {


          // dialog dimensions
          let w: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
          let h: string = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0).toString() + 'px';

          const dialogRef: MatDialogRef<GearSelectDialogComponent, any> = this.dialog.open(GearSelectDialogComponent, {
            width: SCREEN_WIDTH > w ? SCREEN_WIDTH : w,
            height: SCREEN_HEIGHT > h ? SCREEN_HEIGHT : h,
            data: { screen: "Settings", gearIndex: index }
          });

          dialogRef.afterClosed().subscribe((result: any) => {

            if (result >= 0) {
              localStorage.setItem('last_gear', result.gearID);
            }
          });
        }

        // delete gear
        else if (result == 'delete') {
          let id = currGear.gearID;

          if (this.dataSync.user_trip().sets.some(s => s.gear.gearID == id)) {
            console.log('Cannot delete');
            this.toastr.error('Gear is currently attached to one or more set(s), cannot delete.', '', { timeOut: 5000, extendedTimeOut: 5000 });
            return;
          } else {
            const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = this.dialog.open(ConfirmDialogComponent, {
              autoFocus: false,
              data: { screen: 'deletegear' },
            });
  
            // close confirm dialog, delete gear
            dialogRef.afterClosed().subscribe((result: boolean) => {
              if (result) {
                this.dataSync.deleteGear(id).then((res: boolean) => {
                  if (res) {
                    this.dataSync.user_gear.update(gearList => gearList.filter(g => g.gearID != id));
                    this.toastr.success("Successfully deleted gear " + currGear.identifier, "Success!");
                  } else {
                    this.toastr.error("Could not delete gear " + currGear.identifier, "Failure");
                  }
                });
              }
            });
          }         
        }
      }
    });
  }


  /**
   * Sync data, that is just reload since sync happens automatically then.
   */
  performDataSync(): void {
    if (this.onlineStatus.getStatus() == OnlineStatusType.OFFLINE) this.toastr.error('Could not sync, check connection', 'Sync failed');
    else {
      this.toastr.success('Initiating Sync..', '');
      this.dataSync.syncUserDatabase(true)
    }
  }

}
